import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CommunityPostTypeFilter, CommunitySortMode } from '@/lib/communityPostData'

export interface BoardUrlFilterState {
  selectedLocations: string[]
  sortMode: CommunitySortMode
  postTypeFilter: CommunityPostTypeFilter
}

function normalizeSortMode(raw: string | null): CommunitySortMode {
  return raw === 'recent' ? 'recent' : 'topWeek'
}

function normalizePostTypeFilter(raw: string | null): CommunityPostTypeFilter {
  if (
    raw === 'flash_drop' ||
    raw === 'new_tattoo' ||
    raw === 'deal' ||
    raw === 'general'
  ) {
    return raw
  }
  return 'all'
}

const EMPTY_SELECTED_LOCATIONS: readonly string[] = []

export function useBoardUrlFilters() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const allLocationsText = t('onskin.filters.allLocations')
  const searchParamsKey = searchParams.toString()

  const filters = useMemo<BoardUrlFilterState>(() => {
    const locationParam = searchParams.get('location')
    const cityLegacy = searchParams.get('city')

    let selectedLocations: string[]
    if (locationParam) {
      selectedLocations = locationParam.split(',').filter(Boolean)
    } else if (cityLegacy) {
      selectedLocations = [cityLegacy]
    } else {
      selectedLocations = [...EMPTY_SELECTED_LOCATIONS]
    }

    return {
      selectedLocations,
      sortMode: normalizeSortMode(searchParams.get('sort')),
      postTypeFilter: normalizePostTypeFilter(searchParams.get('postType')),
    }
  }, [searchParamsKey])

  const applyBoardFilters = useCallback(
    (updatedFilters: BoardUrlFilterState) => {
      const newSearchParams = new URLSearchParams(searchParams)

      if (updatedFilters.selectedLocations.length === 0) {
        newSearchParams.delete('location')
      } else {
        newSearchParams.set('location', updatedFilters.selectedLocations.join(','))
      }
      newSearchParams.delete('city')

      if (updatedFilters.sortMode === 'topWeek') {
        newSearchParams.delete('sort')
      } else {
        newSearchParams.set('sort', updatedFilters.sortMode)
      }

      if (updatedFilters.postTypeFilter === 'all') {
        newSearchParams.delete('postType')
      } else {
        newSearchParams.set('postType', updatedFilters.postTypeFilter)
      }

      const newUrlString = newSearchParams.toString()
      const currentUrlString = searchParams.toString()
      if (newUrlString !== currentUrlString) {
        setSearchParams(newSearchParams, { replace: true })
      }
    },
    [searchParamsKey, searchParams, setSearchParams],
  )

  return {
    ...filters,
    allLocationsText,
    applyBoardFilters,
  }
}
